import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Empty, PageHeader, Rows } from "@/features/admin/parts";
import type { Schemas } from "@/lib/api/client";
import {
  adminAnnouncementsQuery,
  useCreateAnnouncement,
  useDeactivateAnnouncement,
} from "@/lib/api/queries";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/announcements")({
  loader: ({ context }) => context.queryClient.ensureQueryData(adminAnnouncementsQuery),
  component: AnnouncementsPage,
});

type Announcement = Schemas["AnnouncementOut"];

const DURATION = [
  { hours: 0, label: "Пока не сниму" },
  { hours: 24, label: "Сутки" },
  { hours: 24 * 3, label: "3 дня" },
  { hours: 24 * 7, label: "Неделя" },
] as const;

const LEVELS: { value: Announcement["level"]; label: string }[] = [
  { value: "info", label: "Обычное" },
  { value: "warning", label: "Важное" },
];

function AnnouncementsPage() {
  const { data } = useSuspenseQuery(adminAnnouncementsQuery);
  return (
    <>
      <PageHeader title="Объявления" />
      <p className="mt-2 text-sm text-muted">
        Баннер над кабинетом у всех, кто вошёл. Каждый может скрыть его у себя.
      </p>
      <CreateForm />
      <div className="mt-8">
        {data.length === 0 ? (
          <Empty>Объявлений ещё не было.</Empty>
        ) : (
          <Rows>
            {data.map((announcement) => (
              <AnnouncementRow key={announcement.id} announcement={announcement} />
            ))}
          </Rows>
        )}
      </div>
    </>
  );
}

function CreateForm() {
  const create = useCreateAnnouncement();
  const [text, setText] = useState("");
  const [level, setLevel] = useState<Announcement["level"]>("info");
  const [hours, setHours] = useState<number>(0);

  return (
    <form
      className="mt-6 grid gap-4 rounded-2xl border border-line bg-surface p-4"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate(
          {
            text,
            level,
            expires_at: hours ? new Date(Date.now() + hours * 3_600_000).toISOString() : null,
          },
          {
            onSuccess: () => {
              setText("");
              toast.success("Объявление опубликовано");
            },
          },
        );
      }}
    >
      <Field label="Текст">
        {(control) => (
          <textarea
            {...control}
            value={text}
            maxLength={1000}
            rows={3}
            required
            placeholder="Например: сегодня в 23:00 сервер перезагрузится на пару минут"
            onChange={(e) => setText(e.target.value)}
            className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-base"
          />
        )}
      </Field>
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Вид">
          {(control) => (
            <select
              {...control}
              value={level}
              onChange={(e) => setLevel(e.target.value as Announcement["level"])}
              className="h-12 w-full rounded-xl border border-line bg-surface px-3 text-base"
            >
              {LEVELS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Показывать">
          {(control) => (
            <select
              {...control}
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="h-12 w-full rounded-xl border border-line bg-surface px-3 text-base"
            >
              {DURATION.map((option) => (
                <option key={option.hours} value={option.hours}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Button type="submit" pending={create.isPending}>
          Опубликовать
        </Button>
      </div>
      <FormError message={create.error?.message} />
    </form>
  );
}

function AnnouncementRow({ announcement }: { announcement: Announcement }) {
  const deactivate = useDeactivateAnnouncement();
  const state = announcement.visible
    ? announcement.expires_at
      ? `показывается до ${formatDateTime(announcement.expires_at)}`
      : "показывается"
    : announcement.is_active
      ? "срок истёк"
      : "снято";

  return (
    <li className="grid gap-2 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-4">
      <div className="min-w-0">
        <p className={cn("whitespace-pre-line", !announcement.visible && "text-muted")}>
          {announcement.level === "warning" && (
            <span className="mr-2 text-sm text-amber-text">важное</span>
          )}
          {announcement.text}
        </p>
        <p className="mt-0.5 text-sm text-muted">
          {formatDateTime(announcement.created_at)} · {state}
        </p>
      </div>
      {announcement.visible && (
        <Button
          size="sm"
          variant="ghost"
          pending={deactivate.isPending}
          onClick={() =>
            deactivate.mutate(announcement.id, {
              onSuccess: () => toast.success("Объявление снято"),
              onError: (error) => toast.error(error.message),
            })
          }
        >
          Снять
        </Button>
      )}
    </li>
  );
}
